import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";

import { PAGES } from "src/constants/pages";
import { OTP_METHODS, OTP_LOGIN_FLOWS } from "src/constants/otp";
import SimpleOTPInput from "src/components/global/SimpleOtpInput/SimpleOtpInput";
import HtmlContentComponent from "src/components/global/HtmlContent/HtmlContentComponent";
import {
  login_mfa_otp_request_post,
  login_mfa_otp_validation_post,
} from "src/actions/loginAction";
import {
  UNEXPECTED_ERROR,
  OTP_ERROR_DISPLAY,
  RESPONSE_CODE,
} from "src/constants/errors";
import { utils_user } from "src/utils/utils_user.js";
import { opt_selected_method } from "src/utils/validationrules_api_app";
import { utils_validation } from "src/utils/utils_validation";
import { utils } from "src/utils/utils_general";
import { LOCAL_STORAGE } from "src/constants/localStorage";

function formatPhone(phone) {
  const lastDigits = phone.slice(-4);
  return `(***) ***-${lastDigits}`;
}

function MfaOtpValidation(props) {
  const {
    otpId,
    error,
    setError,
    goToGetNewOTPCode,
    isMFAFlow,
    contactDetail,
  } = props;
  const isAppLoading = useSelector((state) => state.spinner);

  const initialOtpDigits = ["", "", "", "", "", ""];
  const [otpDigits, setOtpDigits] = useState(initialOtpDigits);
  const dispatch = useDispatch();

  const submitMfaOtp = async (otp) => {
    const resultPromise = dispatch(login_mfa_otp_validation_post(otpId, otp));
    resultPromise
      .then(() => {
        this.props.history.push(PAGES.DASHBOARD);
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.msg;
        if (errorMessage === "incorrect otp code") {
          return setError(
            "The code you entered is incorrect. Please try again.",
          );
        }
        if (errorMessage === "Reached maximum limit") {
          return setError(
            "You have reached the maximum validation limit for OPT, generate a new code.",
          );
        }
        if (errorMessage === "invalid magiclink token") {
          return setError(
            "The code you entered is incorrect. Please try again.",
          );
        } else {
          return setError(UNEXPECTED_ERROR);
        }
      });
  };

  const handleSubmitOtp = (e) => {
    e.preventDefault();
    setError("");
    const otp = otpDigits.join("");
    if (otp.length === 6) {
      submitMfaOtp(otp);
    }
  };

  const handleOtpPaste = (value) => {
    setOtpDigits(String(value).split(""));
    setError("");
  };

  const handleOtpChange = (value) => {
    setOtpDigits(value);
    setError("");
  };

  const getNewOTPCode = (requestOTPwithoutPopup) => {
    setOtpDigits(initialOtpDigits);
    setError("");
    goToGetNewOTPCode(requestOTPwithoutPopup);
  };

  const isSubmitEnabled = () => {
    const isOtpFilled = otpDigits.join("").length === 6;
    return !isAppLoading && isOtpFilled;
  };

  return (
    <section>
      <div className="mfa-wrapper">
        <div>
          <h2>An Authentication Code Has been sent</h2>
          <p className="mb-0">
            In addition to your username and password, you’ll have to enter a
            code to sign in to your account.{" "}
          </p>
          <HtmlContentComponent
            markup={"We have sent {contact_detail} a code. Please enter it below.".replace(
              "{contact_detail}",
              contactDetail,
            )}
          />
        </div>

        <div>
          <Form noValidate onSubmit={handleSubmitOtp}>
            <div className="py-3">
              <SimpleOTPInput
                handlePaste={handleOtpPaste}
                handleChange={handleOtpChange}
                token={otpDigits}
                inputType="number"
              />
            </div>
            <div className="text-right py-4">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  getNewOTPCode(true);
                }}
              >
                Re-Send code?
              </a>
            </div>

            <div className="text-right">
              {isMFAFlow && (
                <Button
                  variant={"link"}
                  className="btn-back me-30"
                  onClick={() => getNewOTPCode(false)}
                >
                  Back
                </Button>
              )}
              <Button
                variant={isSubmitEnabled() ? "primary" : "disabled"}
                type="submit"
              >
                Continue
              </Button>
            </div>

            {error && (
              <div className="text-center py-2">
                <Form.Text
                  className="text-danger form-error"
                  aria-live="polite"
                >
                  {error}
                </Form.Text>
              </div>
            )}
          </Form>
        </div>
      </div>
    </section>
  );
}

function MfaOtpSelectMethod(props) {
  const { error, setError } = props;
  const [method, setMethod] = useState("");

  const onMethodSelected = (event) => {
    setMethod(event.currentTarget.id);
  };

  const onSubmitSelectedMethod = (event) => {
    event.preventDefault();
    setError("");
    const errors = utils_validation.validate(opt_selected_method, { method });
    if (utils.is_obj_empty(errors)) {
      props.onSelectedMethod(method);
    } else {
      setError("opt-method-field-" + errors.method);
    }
  };

  return (
    <section>
      <div className="mfa-wrapper">
        <div>
          <h1>Authenticate your Account</h1>
          <p>
            In addition to your username and password, you’ll have to enter a
            code to sign in to your account.
          </p>
          <strong>Please select your verification method</strong>
        </div>

        <div>
          <Form noValidate onSubmit={onSubmitSelectedMethod}>
            <div className="py-3">
              <div className="radio-round">
                <input
                  type="radio"
                  id={OTP_METHODS.CALL}
                  name="method"
                  onChange={onMethodSelected}
                />
                <label htmlFor={OTP_METHODS.CALL}>Call me</label>
              </div>
              <div className="radio-round">
                <input
                  type="radio"
                  id={OTP_METHODS.SMS}
                  name="method"
                  onChange={onMethodSelected}
                />
                <label htmlFor={OTP_METHODS.SMS}>Text Me</label>
              </div>
              <div className="radio-round">
                <input
                  type="radio"
                  id={OTP_METHODS.EMAIL}
                  name="method"
                  onChange={onMethodSelected}
                />
                <label htmlFor={OTP_METHODS.EMAIL}>Email</label>
              </div>
            </div>

            <div className="text-right">
              <Button className="btn-full" variant={"primary"} type="submit">
                Continue
              </Button>
            </div>

            {error && (
              <div className="text-center py-2">
                <Form.Text
                  className="text-danger form-error"
                  aria-live="polite"
                >
                  {error}
                </Form.Text>
              </div>
            )}
          </Form>
        </div>
      </div>
    </section>
  );
}

function LimitReached() {
  return (
    <section>
      <div className="mfa-wrapper">
        <div>
          <h1>Limit Reached</h1>
          <p className=" mb-2 warning-text">
            You have reached the maximum number of Re-Sends for this number.
          </p>
          <p className=" mb-2 warning-text">Please try again in 30 minutes.</p>
          <div className="text-right py-4">
            <Link to={PAGES.LOGIN}>Sign in</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function MfaOtp() {
  const user = utils_user.get_user();
  const isMFAFlow = false; // false in portal because here we're gonna use now only email method, don't show the method selector
  const [step, setStep] = useState("request"); // 'request', 'validation', 'limit-reached'
  const [methodSelected, setMethodSelected] = useState(
    isMFAFlow ? false : true,
  );
  const [otpId, setOtpId] = useState(null);
  const [error, setError] = useState("");
  const [contactDetail, setContactDetail] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    const otpId = utils.get_local_storage(LOCAL_STORAGE.OTP_ID);
    if (otpId) {
      setStep("validation");
      setOtpId(otpId);
      setMethodSelected(true);
    } else if (!isMFAFlow) {
      sendOTPCode(OTP_METHODS.EMAIL);
    }
  }, []);

  const sendOTPCode = (method) => {
    const payload = {
      method,
      login_flow: OTP_LOGIN_FLOWS.PASSWORD,
    };
    const contactDetail =
      method === OTP_METHODS.EMAIL ? user?.email : formatPhone(user?.ph_mobile);
    setContactDetail(contactDetail);
    const resultPromise = dispatch(login_mfa_otp_request_post(payload));
    resultPromise
      .then((res) => {
        setStep("validation");
        if (res.otp_id) {
          setOtpId(res.otp_id);
          utils.set_local_storage(LOCAL_STORAGE.OTP_ID, res.otp_id);
          setMethodSelected(true);
        } else {
          setError(UNEXPECTED_ERROR);
        }
      })
      .catch((error) => {
        if (error.response?.status === RESPONSE_CODE["405_data_invalid"]) {
          setStep("validation");
          setError(OTP_ERROR_DISPLAY.CODE_INCORRECT);
          return;
        }
        if (error.response?.status === RESPONSE_CODE["490_max_reached"]) {
          setStep("limit-reached");
          return;
        }
        setStep("validation");
        setError(UNEXPECTED_ERROR);
      });
  };

  const newOTPCode = (requestOTPwithoutPopup) => {
    if (!isMFAFlow || requestOTPwithoutPopup) {
      setStep("validation");
      setMethodSelected(true);
      sendOTPCode(OTP_METHODS.EMAIL);
    } else if (isMFAFlow) {
      setStep("request");
      setMethodSelected(false);
    }
    setOtpId(null);
    utils.remove_local_storage(LOCAL_STORAGE.OTP_ID);
  };

  const onSelectedMethod = (method) => {
    sendOTPCode(method);
  };

  if (isMFAFlow && !methodSelected) {
    return (
      <MfaOtpSelectMethod
        onSelectedMethod={onSelectedMethod}
        error={error}
        setError={setError}
      />
    );
  }

  if (methodSelected) {
    return (
      <>
        {step === "validation" ? (
          <MfaOtpValidation
            otpId={otpId}
            error={error}
            setError={setError}
            contactDetail={contactDetail}
            goToGetNewOTPCode={newOTPCode}
            isMFAFlow={isMFAFlow}
          />
        ) : null}
        {step === "limit-reached" ? <LimitReached /> : null}
      </>
    );
  }
}
